import axios from '../config/api'
import Vue from 'vue'
import moment from 'moment'
import {
  orderDetailTransform,
  createOrderPayloadTransform,
  createVipOrderPayloadTransform,
} from '@/transforms/orderTransform'
import router from '@/router'
import MERCURE_TOPIC from '../config/mercure'
import { useAxiosErrorHandler } from '@/composables/useAxiosErrorHandler'

const campaignType = process.env.VUE_APP_CAMPAIGN_TYPE ?? 'BNN'

const state = () => ({
  data: [],
  eventSources: {},
  payload: null,
  isError: false,
  isExpired: false,
  isLoading: false,
  submit: {
    data: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
  },
  cancelVipOrder: {
    data: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
  },
  checkConfirmRetroactivePayment: {
    data: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
  },
  confirmRetroactivePayment: {
    data: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
  },
})

// getters
const getters = {
  total: state => state.data.total_price || 0,
  orderDetail: state => state.data,
  orderStatus: state => state.data.status || '',
  isLoading: state => key => state[key].isLoading || false,
  isSuccess: state => key => state[key].isSuccess || false,
  isError: state => key => state[key].isError || false,
  errorMessage: state => key => state[key].errorMessage || '',
}

// actions
const actions = {
  async getOrder({ commit }, { uuid, slot_table, payment_decision }) {
    commit('setOrder')
    await axios
      .get(`/orders/${uuid}`)
      .then(response => {
        commit('setOrderSuccess', {
          ...response.data.data,
          slot_table: slot_table ?? null,
          payment_decision: payment_decision,
        })
      })
      .catch(error => {
        commit('setOrderError')

        if (
          !error.response?.status ||
          error.response?.status === 500 ||
          error.response?.status === 410
        ) {
          router.push('/404')
        }
      })
  },

  /* Subscribe to order status updates with Mercure SSE */
  subscribeOrderStatus({ commit, state }, uuid) {
    // Check if already subscribed and connecting
    if (
      uuid &&
      state.eventSources[uuid] &&
      state.eventSources[uuid].readyState === 0
    ) {
      return
    }

    const hubUrl = process.env.VUE_APP_HUB_MERCURE_URL
    const env =
      process.env.NODE_ENV !== 'production' ? 'dev' : 'production'
    const topic = `${env}.${MERCURE_TOPIC.ORDER_STATUS}.${uuid}`

    const url = `${hubUrl}?topic=${topic}`

    const es = new EventSource(url)

    state.eventSources[uuid] = es

    state.eventSources[uuid].onmessage = event => {
      const data = JSON.parse(event.data)

      if (data.data.status) {
        commit('setOrderStatusSuccess', data.data.status)
      }
    }
  },
  unsubscribeOrderStatus({ state }, uuid) {
    if (uuid && state.eventSources[uuid]) {
      state.eventSources[uuid].close()
      delete state.eventSources[uuid]
    }
  },

  async getOrderLists({ commit }) {
    const headers = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('tokenOTP')}`,
      },
    }

    commit('getOrderListsRequest')
    await axios
      .get(`/user/order-history/${campaignType}`, headers)
      .then(response => {
        commit('getOrderListsRequestSuccess', response.data.data)
      })
      .catch(() => {
        commit('getOrderListsRequestFailure')
        localStorage.removeItem('tokenOTP')
      })
  },

  getOrderDetail({ commit }, { uuid, slot_table }) {
    commit('setOrder')
    axios
      .get(`/user/order-detail/${uuid}`)
      .then(response => {
        commit('setOrderSuccess', {
          ...response.data.data,
          slot_table: slot_table ?? null,
        })
      })
      .catch(() => {
        commit('setOrderError')
      })
    // }
  },

  async extendTimePayAtStore({ commit }, payload) {
    commit('submitRequest')
    await axios
      .post('/user/extend-pay-at-store', payload)
      .then(() => {
        commit('submitRequestSuccess', null)

        Vue.$toast.success('ขยายเวลาชำระเงินสำเร็จ', {
          duration: 5000,
          position: 'top',
        })
      })
      .catch(error => {
        commit('submitRequestFailure')

        let message = error.response.data.message
          ? error.response.data.message
          : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'

        Vue.$toast.error(message, {
          duration: 5000,
          position: 'top',
        })
      })
  },

  isPayAtStoreExpired({ commit }) {
    commit('setOrderExpired')
  },

  async confirmPayment({ commit }, payload) {
    commit('submitRequest')

    const accessToken = Vue.prototype.$cookies.get('token')
    // localStorage.getItem('token')

    if (!accessToken) {
      Vue.$toast.error('กรุณาเข้าสู่ระบบ', {
        duration: 5000,
        position: 'top',
      })
      return
    }

    await axios
      .post('/orders/stamp-pay-at-store', payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then(() => {
        commit('submitRequestSuccess', null)

        Vue.$toast.success('ยืนยันการชำระเงินสำเร็จ', {
          duration: 5000,
          position: 'top',
        })
      })
      .catch(error => {
        const message = useAxiosErrorHandler().handleError(error)
        commit('submitRequestFailure', message)
      })
  },

  async checkConfirmRetroactivePayment({ commit }, payload) {
    commit('checkConfirmRetroactivePaymentRequest')

    const accessToken = Vue.prototype.$cookies.get('token')

    if (!accessToken) {
      Vue.$toast.error('กรุณาเข้าสู่ระบบ', {
        duration: 5000,
        position: 'top',
      })
      return
    }

    await axios
      .post('/orders/repair-pay-at-store/check', payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then(response => {
        commit(
          'checkConfirmRetroactivePaymentSuccess',
          response.data.data,
        )
      })
      .catch(error => {
        const message = useAxiosErrorHandler().handleError(error)
        commit('checkConfirmRetroactivePaymentFailure', message)

        if (!error.response?.status || error.response?.status > 200) {
          return router.push({
            name: 'NotFound',
          })
        }
      })
  },

  async confirmRetroactivePayment({ commit }, payload) {
    commit('confirmRetroactivePaymentRequest')

    const accessToken = Vue.prototype.$cookies.get('token')

    if (!accessToken) {
      Vue.$toast.error('กรุณาเข้าสู่ระบบ', {
        duration: 5000,
        position: 'top',
      })
      return
    }

    await axios
      .post('/orders/repair-pay-at-store/stamp', payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then(response => {
        commit('confirmRetroactivePaymentSuccess', response.data.data)

        Vue.$toast.success('ซ่อมการชำระเงินสำเร็จ', {
          duration: 5000,
          position: 'top',
        })
      })
      .catch(error => {
        const message = useAxiosErrorHandler().handleError(error)
        commit('confirmRetroactivePaymentFailure', message)
      })
  },

  async inquiryTransactionRequest({ commit }, uuid) {
    commit('submitRequest')

    const transformedPayload = {
      order_id: uuid,
    }

    await axios
      .post('/orders/inquiry', transformedPayload)
      .then(res => {
        const transformedData = {
          message: res.data.data.message,
        }

        commit('submitRequestSuccess', transformedData)
      })
      .catch(error => {
        commit('submitRequestFailure', error.response.data.message)

        let message = error.response.data.message
          ? error.response.data.message
          : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'

        Vue.$toast.error(message, {
          duration: 5000,
          position: 'top',
        })
      })
  },

  async createOrder(
    { commit, rootState },
    { payload, is_vip, confirm_cancel_pending = false },
  ) {
    commit('submitRequest')

    const queryParam = router.currentRoute.query
    const coupons = rootState.cart?.coupons

    // Ensure the delivery method is correct check from store
    let deliveryMethod = {
      // Delivery information for home delivery
      is_delivery: payload.is_delivery,
      delivery: payload.delivery,

      // Branch information for store pickup
      branch_time_id: payload.branch_time_id,
      branch_province: payload.branch_province,
    }

    if (rootState.cart?.delivery == 'delivery') {
      deliveryMethod = {
        // Force delivery information since delivery is selected
        is_delivery: true,
        delivery: true,

        // Clear branch information since delivery is selected
        branch_time_id: null,
        branch_province: null,
      }
    } else if (rootState.cart?.delivery == 'store') {
      deliveryMethod = {
        ...deliveryMethod,

        // Clear delivery information since store pickup is selected
        is_delivery: false,
        delivery: false,
      }
    }

    const transformed = is_vip
      ? createVipOrderPayloadTransform(payload, {
          coupons,
          queryParam,
        })
      : createOrderPayloadTransform(
          {
            ...payload,
            ...deliveryMethod,
          },
          {
            coupons,
            queryParam,
          },
        )

    if (confirm_cancel_pending && !is_vip) {
      transformed.confirm_cancel_pending = true
    }

    return await axios
      .post('/orders', transformed)
      .then(res => {
        commit('submitRequestSuccess', res.data)

        commit('recaptcha/setReset', true, { root: true })
        setTimeout(() => {
          commit('recaptcha/setReset', false, { root: true })
        }, 3000)

        return res.data
      })
      .catch(error => {
        const message = error.response?.data?.message
          ? error.response.data.message
          : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'

        // Customer already has an unpaid order: let the caller ask for confirmation
        if (
          error.response?.status === 409 &&
          error.response?.data?.data?.error_code ===
            'ORDER_PENDING_EXISTS'
        ) {
          commit('submitRequestFailure', message)

          return {
            pendingExists: true,
            pendingOrders:
              error.response.data.data.pending_orders || [],
            message,
          }
        }

        Vue.$toast.error(message, {
          duration: 5000,
          position: 'top',
        })

        commit('submitRequestFailure', message)
      })
  },

  async submitVipPayment({ commit, state }, { uuid, payload }) {
    commit('submitRequest')

    try {
      const response = await axios.post(
        `/vip-payment/${uuid}/submit`,
        payload,
      )

      commit('submitRequestSuccess', response.data.data)

      // Set order details in the state after successful submission
      commit('setOrderSuccess', {
        ...response.data.data.order,
        is_payment_open: state.data?.is_payment_open ?? false,
      })
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('submitRequestFailure', message)
    }
  },

  async cancelVipOrder({ commit }, { uuid, code }) {
    commit('cancelVipOrderRequest')

    try {
      const response = await axios.post(
        `/vip-orders/${uuid}/cancel`,
        { code },
      )

      commit('cancelVipOrderSuccess', response.data.data)
    } catch (error) {
      const message = useAxiosErrorHandler().handleError(error)
      commit('cancelVipOrderFailure', message)
    }
  },

  updateOrder({ commit }, payload) {
    commit('updateOrderData', payload)
  },
}

// mutations
const mutations = {
  updateOrderData(state, payload) {
    state.data = {
      ...state.data,
      ...payload,
    }
  },
  setOrder(state) {
    state.isLoading = true
    state.isError = false
  },
  setOrderSuccess(state, payload) {
    state.isLoading = false
    state.data = orderDetailTransform(payload)
    state.isError = false
  },
  setOrderError(state) {
    state.isLoading = false
    state.isError = true
  },
  setOrderStatusSuccess(state, payload) {
    state.data = {
      ...state.data,
      status: payload,
    }
  },
  setOrderPaymentMethod(state, payload) {
    state.data = {
      ...state.data,
      ...payload,
    }
  },
  setReceiveBranchName(state, branchName) {
    if (state.data) {
      state.data.receive_branch_name = branchName
    }
  },

  getOrderListsRequest(state) {
    state.isLoading = true
    state.isError = false
  },
  getOrderListsRequestSuccess(state, payload) {
    state.isLoading = false
    state.data = payload
    state.isError = false
  },
  getOrderListsRequestFailure(state) {
    state.isLoading = false
    state.isError = true
  },
  submitRequest(state) {
    state.submit.isLoading = true
    state.submit.isError = false
    state.submit.isSuccess = false
    state.submit.errorMessage = ''
  },
  submitRequestSuccess(state, event) {
    state.submit.data = event
    state.submit.isLoading = false
    state.submit.isSuccess = true
  },
  submitRequestFailure(state, errorMessage) {
    state.submit.isLoading = false
    state.submit.isError = true
    state.submit.errorMessage =
      errorMessage || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
  },
  submitRequestReset(state) {
    state.submit.data = null
    state.submit.isLoading = false
    state.submit.isSuccess = false
    state.submit.isError = false
    state.submit.errorMessage = ''
  },
  cancelVipOrderRequest(state) {
    state.cancelVipOrder.isLoading = true
    state.cancelVipOrder.isError = false
    state.cancelVipOrder.isSuccess = false
    state.cancelVipOrder.errorMessage = ''
  },
  cancelVipOrderSuccess(state, payload) {
    state.cancelVipOrder.data = payload
    state.cancelVipOrder.isLoading = false
    state.cancelVipOrder.isSuccess = true
  },
  cancelVipOrderFailure(state, errorMessage) {
    state.cancelVipOrder.isLoading = false
    state.cancelVipOrder.isError = true
    state.cancelVipOrder.errorMessage =
      errorMessage || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
  },
  checkConfirmRetroactivePaymentRequest(state) {
    state.checkConfirmRetroactivePayment.isLoading = true
    state.checkConfirmRetroactivePayment.isError = false
    state.checkConfirmRetroactivePayment.isSuccess = false
    state.checkConfirmRetroactivePayment.errorMessage = ''
  },
  checkConfirmRetroactivePaymentSuccess(state, payload) {
    state.checkConfirmRetroactivePayment.data = payload
    state.checkConfirmRetroactivePayment.isLoading = false
    state.checkConfirmRetroactivePayment.isSuccess = true
  },
  checkConfirmRetroactivePaymentFailure(state, errorMessage) {
    state.checkConfirmRetroactivePayment.isLoading = false
    state.checkConfirmRetroactivePayment.isError = true
    state.checkConfirmRetroactivePayment.errorMessage =
      errorMessage || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
  },
  confirmRetroactivePaymentRequest(state) {
    state.confirmRetroactivePayment.isLoading = true
    state.confirmRetroactivePayment.isError = false
    state.confirmRetroactivePayment.isSuccess = false
    state.confirmRetroactivePayment.errorMessage = ''
  },
  confirmRetroactivePaymentSuccess(state, payload) {
    state.confirmRetroactivePayment.data = payload
    state.confirmRetroactivePayment.isLoading = false
    state.confirmRetroactivePayment.isSuccess = true
  },
  confirmRetroactivePaymentFailure(state, errorMessage) {
    state.confirmRetroactivePayment.isLoading = false
    state.confirmRetroactivePayment.isError = true
    state.confirmRetroactivePayment.errorMessage =
      errorMessage || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
  },
  setOrderExpired(state) {
    state.isExpired =
      moment().isAfter(
        moment.unix(state.data.pay_at_store_expire_at_timestamp_bkk),
      ) ?? false
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
